import { createPortal } from 'react-dom';
import common from '../../styles/common.module.css';
import styles from '../../styles/WeaponRack.module.css';

export interface ArmourySlotMenuProps {
  /** Rack position the menu was opened on, and where it sits on screen. */
  index: number;
  x: number;
  y: number;
  /** False when the plate under the pointer does not exist (empty rack). */
  onPlate: boolean;
  clipboardName?: string | null;
  /** The reliable route to the editor - a menu cannot rotate away from
      the pointer mid-interaction the way a plate in the drum can. */
  onEdit?: (index: number) => void;
  onDuplicate: (index: number) => void;
  onCopy: (index: number) => void;
  onCut: (index: number) => void;
  onPaste: (index: number) => void;
  onDelete: (index: number) => void;
  onClose: () => void;
}

/**
 * The rack's right-click menu, PORTALLED to document.body.
 *
 * Two reasons, and both are load-bearing:
 *
 * 1. `WeaponPlate` sets `container-type: inline-size`, which computes to
 *    `contain: layout …` and makes the plate a containing block for
 *    `position: fixed` descendants. A menu rendered inside a plate would
 *    position against the plate's padding box rather than the viewport,
 *    so `clientX`/`clientY` would land somewhere else entirely.
 * 2. `.tabContentArea` is `overflow-y: auto`, and an absolutely-positioned
 *    hover card inside a scroll container causes the hover/reflow loop
 *    recorded in gotchas.md ("the menu trembles").
 *
 * A portal makes both structurally impossible rather than merely avoided.
 *
 * The items are BUTTONS. The grid's menu used bare divs with click
 * handlers and no roles; giving them `role="menuitem"` without making
 * them focusable would have been worse than not labelling them at all.
 */
export default function ArmourySlotMenu({
  index,
  x,
  y,
  onPlate,
  clipboardName,
  onEdit,
  onDuplicate,
  onCopy,
  onCut,
  onPaste,
  onDelete,
  onClose,
}: ArmourySlotMenuProps) {
  // Every item closes the menu after its action - the order the menu's own
  // click used to give, without a click handler on the menu itself.
  const then = (act: (i: number) => void) => () => {
    act(index);
    onClose();
  };
  return createPortal(
    <div className={styles.ctxMenu} style={{ left: x, top: y }} role="menu">
      {onPlate && (
        <>
          {onEdit && (
            <button type="button" role="menuitem" className={common.dropdownItem} onClick={then(onEdit)}>
              Edit weapon
            </button>
          )}
          <button type="button" role="menuitem" className={common.dropdownItem} onClick={then(onDuplicate)}>
            Duplicate
          </button>
          <button type="button" role="menuitem" className={common.dropdownItem} onClick={then(onCopy)}>
            Copy
          </button>
          <button type="button" role="menuitem" className={common.dropdownItem} onClick={then(onCut)}>
            Cut
          </button>
        </>
      )}
      {clipboardName && (
        <button type="button" role="menuitem" className={styles.ctxPaste} onClick={then(onPaste)}>
          Paste {clipboardName}
          {onPlate ? ' before' : ''}
        </button>
      )}
      {onPlate && (
        <>
          <div className={styles.ctxDivider} />
          <button type="button" role="menuitem" className={styles.ctxDanger} onClick={then(onDelete)}>
            Delete
          </button>
        </>
      )}
    </div>,
    document.body,
  );
}
